import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { fetchMySubGroups } from "../../services/firestore/SGloopService";
import { useAuth } from "../../context/AuthContext";
import { TrashIcon, Squares2X2Icon, PlusIcon } from "@heroicons/react/24/solid";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { addDoc, collection, deleteField, doc, getDoc, getFirestore, serverTimestamp, updateDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import ConfirmModal from "./ConfirmModal";


export default function FormsModalv2({ formData, open, onClose, setOpen }) {
    console.log(formData)
    const db = getFirestore(firebaseApp);
    const storage = getStorage();
    const { userData, userRef } = useAuth();
    const [form, setForm] = useState(
        formData || {
            backendVersion: 2,
            title: "",
            description: "",
            sections: [
                {
                    id: uuidv4(),
                    title: "Untitled Section",
                    description: "",
                    excludeForGroupMembers: false, // new boolean
                    questions: [],
                },
            ],
        }
    );

    // 🔧 State for SGloop group search
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredGroups, setFilteredGroups] = useState([]);
    const [allMySubGroups, setAllMySubGroups] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [benefits, setBenefits] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [banner, setBanner] = useState(formData?.banner || null);
    const [bannerFile, setBannerFile] = useState(null);
    const [openClosedModal, setOpenClosedModal] = useState(false);


    // 🔧 Section management
    const addSection = () => {
        setForm((prev) => ({
            ...prev,
            sections: [
                ...prev.sections,
                { id: uuidv4(), title: "Untitled Section", description: "", excludeForGroupMembers: false, questions: [] },
            ],
        }));
    };

    const updateSectionField = (sectionId, field, value) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === sectionId ? { ...s, [field]: value } : s
            ),
        }));
    };

    const deleteSection = (sectionId) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.filter((s) => s.id !== sectionId),
        }));
    };

    // 🔧 Question management
    const addQuestion = (sectionId) => {
        const newQuestion = {
            id: uuidv4(),
            question: "Untitled Question",
            type: "multipleChoice",
            options: ["Option 1"],
            required: false, // new boolean
            goToSectionType: "next", // next | byAnswer | end
            optionsWithDestinations: [],
        };

        setForm((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === sectionId ? { ...s, questions: [...s.questions, newQuestion] } : s
            ),
        }));
    };

    const updateQuestion = (sectionId, questionId, field, value) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === sectionId
                    ? {
                        ...s,
                        questions: s.questions.map((q) =>
                            q.id === questionId ? { ...q, [field]: value } : q
                        ),
                    }
                    : s
            ),
        }));
    };

    const deleteQuestion = (sectionId, questionId) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === sectionId
                    ? {
                        ...s,
                        questions: s.questions.filter((q) => q.id !== questionId),
                    }
                    : s
            ),
        }));
    };

    const addOption = (sectionId, questionId) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === sectionId
                    ? {
                        ...s,
                        questions: s.questions.map((q) =>
                            q.id === questionId
                                ? { ...q, options: [...q.options, `Option ${q.options.length + 1}`] }
                                : q
                        ),
                    }
                    : s
            ),
        }));
    };

    const deleteOption = (sectionId, questionId, optionIdx) => {
        setForm((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === sectionId
                    ? {
                        ...s,
                        questions: s.questions.map((q) =>
                            q.id === questionId
                                ? {
                                    ...q,
                                    options: q.options.filter((_, i) => i !== optionIdx),
                                    optionsWithDestinations: q.optionsWithDestinations.filter(
                                        (o) => o.option !== q.options[optionIdx]
                                    ),
                                }
                                : q
                        ),
                    }
                    : s
            ),
        }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBannerFile(file); // store file for uploading
            setBanner(URL.createObjectURL(file)); // preview
        }
    };

    // Firestore save logic
    const handleSave = async () => {
        if (!form.title.trim()) return alert("Form title is required!");

        try {
            let bannerUrl = null;

            if (formData?.id) {
                // Editing existing form
                const formRef = doc(db, "forms", formData.id);
                const snap = await getDoc(formRef);
                const currentData = snap.data();
                const newVersion = snap.exists() ? (currentData.version || 1) + 1 : 1;
                bannerUrl = currentData.bannerUrl || null;

                if (bannerFile) {
                    if (currentData.bannerUrl) {
                        try {
                            await deleteObject(ref(storage, currentData.bannerUrl));
                        } catch (err) {
                            console.warn(err);
                        }
                    }
                    const storageRef = ref(storage, `forms/${Date.now()}-${bannerFile.name}`);
                    await uploadBytes(storageRef, bannerFile);
                    bannerUrl = await getDownloadURL(storageRef);
                } else if (banner === null && currentData.bannerUrl) {
                    try {
                        await deleteObject(ref(storage, currentData.bannerUrl));
                    } catch (err) {
                        console.warn(err);
                    }
                    bannerUrl = null;
                }

                const sgloopRef = selectedGroup ? doc(db, selectedGroup.ref.path) : null;

                const updateData = {
                    ...form,
                    bannerUrl: bannerUrl ?? currentData.bannerUrl ?? null,
                    version: newVersion,
                    status: "Active",
                    updatedAt: serverTimestamp(),
                    ...(sgloopRef ? { sgloopGroup: sgloopRef } : { sgloopGroup: deleteField() }),
                    ...(benefits ? { sgloopBenefits: benefits } : { sgloopBenefits: deleteField() }),
                };

                await updateDoc(formRef, updateData);
                alert(`Form updated (v${newVersion})`);
            } else {
                // New form
                if (bannerFile) {
                    const storageRef = ref(storage, `forms/${Date.now()}-${bannerFile.name}`);
                    await uploadBytes(storageRef, bannerFile);
                    bannerUrl = await getDownloadURL(storageRef);
                }

                const sgloopRef = selectedGroup ? doc(db, selectedGroup.ref.path) : null;

                const newData = {
                    ...form,
                    bannerUrl,
                    version: 1,
                    admin: userData.uid,
                    subadmin: [],
                    status: "Active",
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    ...(sgloopRef ? { sgloopGroup: sgloopRef } : {}),
                    ...(benefits ? { sgloopBenefits: benefits } : {}),
                };

                await addDoc(collection(db, "forms"), newData);
                alert("Form created (v1)");
            }

            onClose();
        } catch (err) {
            console.error("Error saving form:", err);
            alert("Failed to save form. Try again.");
        }
    };

    const handleClose = async () => {

        try {
            if (formData) {
                const formRef = doc(db, "forms", formData.id);

                await updateDoc(formRef, {
                    status: "Closed",
                    updatedAt: serverTimestamp(),
                }).then(() => {
                    alert(`Form closed`);
                    onClose();
                });
            }

            setOpen(false);
        } catch (err) {
            console.error("Error saving form:", err);
            alert("Failed to save form. Try again.");
        }
    };


    // 🔧 SGloop group search effect
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredGroups([]);
            return;
        }

        const timeout = setTimeout(async () => {
            setIsLoading(true);

            if (!allMySubGroups) {
                const mySubGroups = await fetchMySubGroups(userRef);
                setAllMySubGroups(mySubGroups);
                setFilteredGroups(
                    mySubGroups.filter(g =>
                        g.subGroupName.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                );
            } else {
                setFilteredGroups(
                    allMySubGroups.filter(g =>
                        g.subGroupName.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                );
            }

            setIsLoading(false);
        }, 400);

        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        const preloadData = async () => {
            if (!formData) return;

            // 🔹 Set banner if exists
            if (formData.bannerUrl) {
                setBanner(formData.bannerUrl);
            }

            // 🔹 Set benefits if exists
            if (formData.sgloopBenefits) {
                setBenefits(formData.sgloopBenefits);
            }

            // 🔹 Set selected group if exists
            if (formData.sgloopGroup?.path) {
                try {
                    const sgloopDocRef = doc(db, formData.sgloopGroup.path);
                    const sgloopSnap = await getDoc(sgloopDocRef);
                    if (sgloopSnap.exists()) {
                        setSelectedGroup({
                            id: sgloopSnap.id,
                            ref: sgloopDocRef,
                            subGroupName: sgloopSnap.data().subGroupName,
                        });
                    }
                } catch (err) {
                    console.warn("Error loading SGloop group:", err);
                }
            }
        };

        preloadData();
    }, [formData, open]);

    useEffect(() => {
    if (formData) {
        setForm({
            backendVersion: formData.backendVersion || 2,
            title: formData.title || "",
            description: formData.description || "",
            sections: formData.sections || [],
        });
    }
}, [formData, open]);



    if (!open) return null;

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            {openClosedModal && (<ConfirmModal
                onClose={() => setOpenClosedModal(false)}
                onConfirm={handleClose}
                confirmTitle="Close Form"
                confirmSubtitle="Confirming will close the form and make it no longer publicly accessible."
            ></ConfirmModal>)}
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full mx-4 my-10 overflow-y-auto max-h-[90vh] animate-fadeIn">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">

                    <h2 className="text-xl font-semibold text-gray-800">
                        Form Builder (v2)
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition"
                    >
                        X
                    </button>
                </div>

                <div className="px-6 py-6 space-y-8">
                    {/* Form Title + Description */}

                    {/* Banner Upload */}
                    <div className="mb-4">
                        {banner ? (
                            <div className="relative">
                                <img
                                    src={banner}
                                    alt="Form banner"
                                    className="w-full max-h-60 object-contain rounded-lg border bg-gray-100"
                                />
                                <button
                                    type="button"
                                    onClick={() => setBanner(null)}
                                    className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                                >
                                    <TrashIcon className="h-4 w-4 text-red-500" />
                                </button>
                            </div>
                        ) : (
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                                <PhotoIcon className="w-8 h-8 text-gray-400" />
                                <span className="mt-1 text-sm text-gray-500">Upload Banner</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>

                    {/* Form Title + Description */}
                    <div className="space-y-3">
                        <input
                            className="w-full text-2xl font-semibold border-b border-gray-300 focus:border-blue-500 outline-none pb-1"
                            placeholder="Form title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />
                        <textarea
                            className="w-full border rounded-lg p-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                            placeholder="Form description..."
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </div>



                    {/* Sections */}
                    {form.sections.map((section) => (
                        <div
                            key={section.id}
                            className="border border-gray-200 rounded-xl p-5 shadow-sm bg-gray-50"
                        >
                            {/* Section Header */}
                            <div className="flex justify-between items-center mb-4">
                                <input
                                    className="w-full text-lg font-medium border-b border-gray-300 focus:border-blue-500 outline-none pb-1"
                                    placeholder="Section title"
                                    value={section.title}
                                    onChange={(e) =>
                                        updateSectionField(section.id, "title", e.target.value)
                                    }
                                />
                                <button
                                    onClick={() => deleteSection(section.id)}
                                    className="text-red-500 hover:text-red-700 ml-2"
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>
                            <textarea
                                className="w-full border rounded-lg p-2 text-sm text-gray-700 focus:ring-2 focus:ring-blue-400 focus:border-transparent mb-2"
                                placeholder="Section description..."
                                value={section.description}
                                onChange={(e) =>
                                    updateSectionField(section.id, "description", e.target.value)
                                }
                            />

                            {/* Section boolean: exclude for group members */}
                            <label className="flex items-center gap-2 mb-4">
                                <input
                                    type="checkbox"
                                    checked={section.excludeForGroupMembers}
                                    onChange={(e) =>
                                        updateSectionField(
                                            section.id,
                                            "excludeForGroupMembers",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span className="text-sm text-gray-700">
                                    Exclude this section for group members
                                </span>
                            </label>

                            {/* Questions */}
                            {section.questions.map((q) => (
                                <div
                                    key={q.id}
                                    className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <input
                                            className="w-3/4 font-medium text-gray-800 border-b border-gray-300 focus:border-blue-500 outline-none pb-1"
                                            placeholder="Untitled question"
                                            value={q.question}
                                            onChange={(e) =>
                                                updateQuestion(section.id, q.id, "question", e.target.value)
                                            }
                                        />
                                        <div className="flex gap-2">
                                            <select
                                                className="border rounded-lg px-2 py-1 text-sm text-gray-700 focus:ring-2 focus:ring-blue-400"
                                                value={q.type}
                                                onChange={(e) =>
                                                    updateQuestion(section.id, q.id, "type", e.target.value)
                                                }
                                            >
                                                <option value="shortAnswer">Short answer</option>
                                                <option value="paragraph">Paragraph</option>
                                                <option value="multipleChoice">Multiple choice</option>
                                                <option value="checkboxes">Checkboxes</option>
                                            </select>
                                            <button
                                                onClick={() => deleteQuestion(section.id, q.id)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Question boolean: required */}
                                    <label className="flex items-center gap-2 mb-2">
                                        <input
                                            type="checkbox"
                                            checked={q.required}
                                            onChange={(e) =>
                                                updateQuestion(section.id, q.id, "required", e.target.checked)
                                            }
                                        />
                                        <span className="text-sm text-gray-700">Required question</span>
                                    </label>

                                    {/* Options */}
                                    {(q.type === "multipleChoice" || q.type === "checkboxes") && (
                                        <div className="space-y-2">
                                            {q.options.map((opt, idx) => (
                                                <div key={idx} className="flex items-center gap-2">
                                                    <input
                                                        type={q.type === "checkboxes" ? "checkbox" : "radio"}
                                                        disabled
                                                        className="text-blue-500"
                                                    />
                                                    <input
                                                        className="border rounded px-2 py-1 w-full text-sm"
                                                        value={opt}
                                                        onChange={(e) => {
                                                            const newOptions = [...q.options];
                                                            newOptions[idx] = e.target.value;
                                                            updateQuestion(section.id, q.id, "options", newOptions);
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => deleteOption(section.id, q.id, idx)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                onClick={() => addOption(section.id, q.id)}
                                                className="text-blue-600 text-sm font-medium hover:underline"
                                            >
                                                + Add option
                                            </button>
                                        </div>
                                    )}
                                    {q.type === "multipleChoice" && (
                                        <div className="mt-4">
                                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                                After this question:
                                            </label>
                                            <select
                                                className="w-full border rounded px-2 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                                                value={q.goToSectionType}
                                                onChange={(e) =>
                                                    updateQuestion(section.id, q.id, "goToSectionType", e.target.value)
                                                }
                                            >
                                                <option value="next">Continue to next section</option>
                                                <option value="byAnswer">Go to section based on answer</option>
                                                <option value="end">Submit form</option>
                                            </select>

                                            {q.goToSectionType === "byAnswer" && (
                                                <div className="mt-3 space-y-2">
                                                    <p className="text-xs text-gray-500">
                                                        Set which section each answer should lead to:
                                                    </p>
                                                    {q.options.map((opt, idx) => (
                                                        <div key={idx} className="flex items-center gap-2">
                                                            <span className="text-sm w-1/3 truncate">{opt}</span>
                                                            <select
                                                                className="border rounded px-2 py-1 text-sm flex-1"
                                                                value={
                                                                    q.optionsWithDestinations.find((o) => o.option === opt)?.goToSection || ""
                                                                }
                                                                onChange={(e) => {
                                                                    const updated = q.optionsWithDestinations.filter(
                                                                        (o) => o.option !== opt
                                                                    );
                                                                    updated.push({
                                                                        option: opt,
                                                                        goToSection: e.target.value,
                                                                    });
                                                                    updateQuestion(section.id, q.id, "optionsWithDestinations", updated);
                                                                }}
                                                            >
                                                                <option value="">Select section</option>
                                                                {form.sections.map((s) => (
                                                                    <option key={s.id} value={s.id}>
                                                                        {s.title}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}


                                    {q.type === "shortAnswer" && (
                                        <input
                                            type="text"
                                            disabled
                                            placeholder="Short answer text"
                                            className="mt-2 w-full border-b border-gray-300 text-sm italic"
                                        />
                                    )}

                                    {q.type === "paragraph" && (
                                        <textarea
                                            disabled
                                            placeholder="Long answer text"
                                            className="mt-2 w-full border border-gray-200 rounded-lg text-sm italic p-2"
                                        />
                                    )}
                                </div>
                            ))}

                            <button
                                onClick={() => addQuestion(section.id)}
                                className="mt-3 bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                            >
                                + Add Question
                            </button>
                        </div>
                    ))}

                    {/* Add section */}
                    <button
                        onClick={addSection}
                        className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-500 hover:text-blue-600 transition"
                    >
                        + Add Section
                    </button>
                    <div className="border-t border-gray-200 my-4"></div>
                    {/* Link to SGloop Group */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-1">Optional: Link to Your SGloop Group</h3>
                        <p className="text-sm text-gray-500 mb-3">
                            You can optionally link this form to one of your SGloop groups. Start typing below to search and select a group. Providing this information is not required.
                        </p>
                        {/* SGloop group search component at end */}
                        <div className="mt-6">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search for your SGloop group (optional)..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setShowDropdown(true);
                                    }}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {showDropdown && searchTerm.trim() !== "" && (
                                    <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 shadow max-h-40 overflow-y-auto">
                                        {isLoading ? (
                                            <p className="p-2 text-gray-500 text-sm">Searching...</p>
                                        ) : filteredGroups.length > 0 ? (
                                            filteredGroups.map((group) => (
                                                <button
                                                    key={group.id}
                                                    onClick={() => {
                                                        setSelectedGroup(group);
                                                        setShowDropdown(false);
                                                    }}
                                                    className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                                                >
                                                    {group.subGroupName}
                                                </button>
                                            ))
                                        ) : (
                                            <p className="p-2 text-gray-500 text-sm">No groups found</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Selected Group */}
                            {selectedGroup && (
                                <div className="mt-4 border rounded-lg p-3 bg-gray-50">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-medium text-sm sm:text-base">{selectedGroup.subGroupName}</h4>
                                        <button
                                            onClick={() => setSelectedGroup(null)}
                                            className="text-xs text-red-600 hover:underline"
                                        >
                                            Remove
                                        </button>
                                    </div>

                                    {/* Benefits Textarea */}
                                    <label className="block mt-3 mb-1 text-sm text-gray-700 font-medium">
                                        Benefits of Joining (optional)
                                    </label>
                                    <textarea
                                        value={benefits}
                                        onChange={(e) => setBenefits(e.target.value)}
                                        placeholder="Describe the benefits of joining this group..."
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                    ></textarea>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center border-t px-6 py-4 bg-gray-50 rounded-b-2xl">
    {/* Left side */}
    <button
        onClick={onClose}
        className="text-gray-600 px-4 py-2 rounded hover:bg-gray-200 transition"
    >
        Cancel
    </button>

    {/* Right side buttons */}
    <div className="flex gap-3"> {/* use gap to space buttons */}
        <button
            onClick={handleSave}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
        >
            Save Form
        </button>
        {formData && (
            <button
                onClick={() => setOpenClosedModal(true)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-red-600 text-white rounded hover:bg-red-700"
            >
                Closed Form
            </button>
        )}
    </div>
</div>

            </div>
        </div>
    );
}
